import fetchWithAuth from "@/api/secureFetch";
import type { NewProductState, NfrKey, SourceConnection, SourceId } from "./state";

const INFER_ENDPOINT = "/api/product-creation/infer-field";
export const INFER_TIMEOUT_MS = 30_000;

export type InferableField = "solution" | "users" | `nfr_${NfrKey}`;

export interface InferFieldResult {
    field: InferableField;
    value?: string;
    values?: string[];
}

/** Request-shaped snapshot of the form: blank list rows dropped, connections
 * reduced to the names of connected sources (rendered as one-line hints
 * server-side). */
export function buildInferStateSnapshot(state: NewProductState) {
    return {
        kind: state.kind,
        scope: state.scope,
        primaryFormFactor: state.primaryFormFactor,
        fields: { ...state.fields },
        nfr: { ...state.nfr },
        users: state.users.filter(u => u.trim() !== ""),
        team: state.team.filter(t => t.trim() !== ""),
        connectedSources: (Object.entries(state.connections) as [SourceId, SourceConnection][])
            .filter(([, conn]) => conn.connected)
            .map(([source]) => source)
    };
}

export async function inferField(
    field: InferableField,
    state: NewProductState,
    token: string,
    options: { signal?: AbortSignal; timeoutMs?: number } = {}
): Promise<InferFieldResult> {
    const controller = new AbortController();
    let timedOut = false;
    const abortFromCaller = () => controller.abort();
    if (options.signal?.aborted) controller.abort();
    else options.signal?.addEventListener("abort", abortFromCaller, { once: true });
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeoutPromise = new Promise<never>((_resolve, reject) => {
        timeoutId = globalThis.setTimeout(() => {
            timedOut = true;
            controller.abort();
            reject(new Error("Inference timed out — try again."));
        }, options.timeoutMs ?? INFER_TIMEOUT_MS);
    });

    try {
        const response = await Promise.race([
            fetchWithAuth(INFER_ENDPOINT, token, {
                method: "POST",
                body: JSON.stringify({ field, state: buildInferStateSnapshot(state) }),
                signal: controller.signal
            }),
            timeoutPromise
        ]);
        if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            throw new Error(body?.error || `Inference failed (${response.status})`);
        }
        return response.json();
    } catch (error) {
        const abortedByInternalTimeout = controller.signal.aborted && !options.signal?.aborted;
        if (timedOut || abortedByInternalTimeout) throw new Error("Inference timed out — try again.");
        throw error;
    } finally {
        if (timeoutId !== undefined) globalThis.clearTimeout(timeoutId);
        options.signal?.removeEventListener("abort", abortFromCaller);
    }
}
